import React from "react";
import { programUrlProblem, PROGRAM_URL_PROBLEM_MESSAGES } from "@/functions-shared/offer/programUrlValidation";

/**
 * KB-4351 — read-only link rows for the admin program and session forms.
 *
 * - `ProgramWebsiteReadOnly`: a session's "Program website", which is a copy of its program's
 *   `programUrl` (edited on the program, never on the session).
 * - `LegacyInformationUrlReadOnly`: a program's old "Information URL" (`url`), which meant both the
 *   program page and the session default. It has no input any more; it stays visible until KB-4353
 *   migrates it, and renders nothing when the program has none.
 */

const ReadOnlyLink: React.FC<{ label: string; url: string; hint: string; testId: string; emptyText?: string }> = ({
  label,
  url,
  hint,
  testId,
  emptyText = "Not set on the program",
}) => (
  <div className="space-y-1" data-testid={testId}>
    <div className="text-sm font-medium leading-none">{label}</div>
    {url ? (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block break-all rounded-md border bg-muted px-3 py-2 text-sm text-primary underline-offset-4 hover:underline"
      >
        {url}
      </a>
    ) : (
      <div className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">{emptyText}</div>
    )}
    <p className="text-sm text-muted-foreground">{hint}</p>
  </div>
);

/**
 * What the session's Program website will be after a save. The program's value wins (the save copies
 * it). When the program has none, the save leaves the offer's stored one alone, so that one is shown,
 * labelled as such; "Not set" is shown only when neither exists.
 */
export const ProgramWebsiteReadOnly: React.FC<{ programUrl: string; storedProgramUrl?: string | null }> = ({
  programUrl,
  storedProgramUrl,
}) => {
  const stored = typeof storedProgramUrl === "string" ? storedProgramUrl.trim() : "";
  // The save copies only a value that passes the rule (`resolveOfferProgramUrl`); say so for one that doesn't.
  const refused = programUrlProblem(programUrl);
  if (refused) {
    return (
      <ReadOnlyLink
        label="Program website"
        url={stored}
        hint={`The program's Program website (${programUrl.trim()}) is not copied: ${PROGRAM_URL_PROBLEM_MESSAGES[refused]}. ${
          stored ? "Saving keeps this session's stored one." : "Fix it on the program."
        }`}
        testId="program-website-readonly"
        emptyText="None: the program's value is not accepted"
      />
    );
  }
  if (!programUrl && stored) {
    return (
      <ReadOnlyLink
        label="Program website"
        url={stored}
        hint="Stored on this session; the program has no Program website yet. Saving keeps it."
        testId="program-website-readonly"
      />
    );
  }
  return (
    <ReadOnlyLink
      label="Program website"
      url={programUrl}
      hint={
        stored && stored !== programUrl
          ? `Copied from the program. Saving replaces this session's stored ${stored}.`
          : "Copied from the program. Edit it on the program."
      }
      testId="program-website-readonly"
    />
  );
};

export const LegacyInformationUrlReadOnly: React.FC<{ url: string | null | undefined }> = ({ url }) => {
  const value = typeof url === "string" ? url.trim() : "";
  if (!value) return null;
  return (
    <ReadOnlyLink
      label="Legacy information URL"
      url={value}
      hint="Read-only. This old field is being split into Program website and Default session website; it is not copied to sessions."
      testId="legacy-information-url"
    />
  );
};
